import React from 'react';
import SEO from '../components/SEO';
import {
  Box,
  Container,
  Heading,
  SimpleGrid,
  Text,
  Image,
  Button,
  VStack,
  HStack,
  Tag,
  Badge,  // Added Badge import
  useColorModeValue,
  LinkBox,
  LinkOverlay,
  Divider,
  Flex,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FaArrowRight } from 'react-icons/fa';

const projectsData = [
  {
    id: 'esp-boardpulse',
    title: 'ESP BoardPulse',
    description: 'A complete testing platform for ESP32 microcontrollers with real-time monitoring and diagnostics.',
    image: '/images/esp-boardpulse.png',
    tags: ['Desktop App', 'Hardware Integration', 'ESP32', 'Arduino'],
    comingSoon: false,  // Added coming soon flag
  },
  {
    id: 'spl-meter',
    title: 'SPL Meter',
    description: 'Sound Pressure Level meter application that measures and analyzes audio levels in real-time.',
    image: '/images/spl-meter.jpg',
    tags: ['Audio Engineering', 'JavaScript', 'Web Audio API'],
    comingSoon: true,  // Added coming soon flag
  },
];

const Projects = () => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const headingColor = useColorModeValue('gray.800', 'white');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const subtleBg = useColorModeValue('gray.50', 'gray.700');
  const accentColor = useColorModeValue('blue.500', 'blue.300');
  const placeholderBg = useColorModeValue('gray.50', 'gray.700'); // Move this to the top

  const baseUrl = "https://abhishekbhalerao.com";

  return (
    <>
      <SEO 
        title="Projects | Abhishek Bhalerao - Full Stack Developer & Robotics Engineer"
        description="Explore my portfolio of projects including ESP BoardPulse and SPL Meter. Full stack development, hardware integration, and audio engineering solutions."
        keywords="Portfolio Projects, ESP BoardPulse, SPL Meter, Full Stack Development, Hardware Integration, Audio Engineering, Desktop Applications"
        ogImage={`${baseUrl}/images/projects_og.jpg`}
        twitterImage={`${baseUrl}/images/projects_og.jpg`}
        canonicalUrl={`${baseUrl}/projects`}
      />

    <Container maxW="container.xl" py={8} mt="120px">
      {/* Header Section */}
      <Box mb={12} textAlign="center">
        <Heading as="h1" size="2xl" mb={4} color={headingColor}>
          My Projects
        </Heading>
        
        <Divider my={6} maxW="100px" borderWidth="2px" borderColor="blue.500" mx="auto" />
        
        <Text 
          fontSize="lg" 
          textAlign="center" 
          mb={10} 
          maxW="800px" 
          mx="auto"
          color={accentColor}
        >
          Here are some of the projects I've worked on, showcasing my skills in software development,
          hardware integration, and audio engineering.
        </Text>
      </Box>
      
      {/* Projects Grid - Always 3 columns on large screens for future expansion */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8} minChildWidth="300px">
        {projectsData.map((project) => (
          <LinkBox
            key={project.id}
            as="article"
            borderRadius="md"
            overflow="hidden"
            bg={bgColor}
            boxShadow="md"
            borderWidth="1px"
            borderColor={borderColor}
            transition="transform 0.3s, box-shadow 0.3s"
            _hover={{ transform: 'translateY(-5px)', boxShadow: 'lg' }}
            position="relative"  // Added for badge positioning
          >
            {/* Coming Soon Badge */}
            {project.comingSoon && (
              <Badge
                position="absolute"
                top="10px"
                right="10px"
                colorScheme="purple"
                fontSize="xs"
                px={2}
                py={1}
                borderRadius="md"
                zIndex="2"
                boxShadow="md"
              >
                Coming Soon
              </Badge>
            )}
            
            {/* Image */}
            <Box height="200px" overflow="hidden" position="relative">
              <Image
                src={project.image}
                alt={project.title}
                objectFit="cover"
                width="100%"
                height="100%"
                transition="transform 0.5s"
                _hover={{ transform: 'scale(1.05)' }}
                filter={project.comingSoon ? 'grayscale(20%)' : 'none'}  // Slight grayscale for coming soon
                fallback={
                  <Box 
                    width="100%" 
                    height="100%" 
                    bg="gray.100" 
                    display="flex" 
                    alignItems="center" 
                    justifyContent="center"
                  >
                    <Text color="gray.500" fontSize="sm">
                      {project.comingSoon ? 'Coming Soon' : 'Image Loading...'}
                    </Text>
                  </Box>
                }
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              {project.comingSoon && (
                <Box
                  position="absolute"
                  top="0"
                  left="0"
                  right="0"
                  bottom="0"
                  bg="blackAlpha.200"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                />
              )}
            </Box>
            
            {/* Content */}
            <VStack align="start" spacing={3} p={5}>
              <Heading as="h3" size="md" lineHeight="1.2">
                {project.comingSoon ? (
                  <Text cursor="default">{project.title}</Text>
                ) : (
                  <LinkOverlay as={RouterLink} to={`/projects/${project.id}`}>
                    {project.title}
                  </LinkOverlay>
                )}
              </Heading>
              
              <Text fontSize="sm" color={textColor} noOfLines={3}>
                {project.description}
              </Text>
              
              <Divider my={2} />
              
              <HStack spacing={2} flexWrap="wrap">
                {project.tags.map((tag) => (
                  <Tag 
                    key={tag} 
                    colorScheme="blue" 
                    size="sm"
                    variant="subtle"
                    mt={1}
                  >
                    {tag}
                  </Tag>
                ))}
              </HStack>
              
              {project.comingSoon ? (
                <Button
                  colorScheme="gray"
                  variant="ghost"
                  size="sm"
                  mt={2}
                  alignSelf="flex-end"
                  cursor="not-allowed"
                  isDisabled
                >
                  Coming Soon
                </Button>
              ) : (
                <Button
                  as={RouterLink}
                  to={`/projects/${project.id}`}
                  rightIcon={<FaArrowRight />}
                  colorScheme="blue"
                  variant="ghost"
                  size="sm"
                  mt={2}
                  alignSelf="flex-end"
                >
                  View Details
                </Button>
              )}
            </VStack>
          </LinkBox>
        ))}
        
        {/* Placeholder for future projects - Optional visual indicator */}
        {projectsData.length < 3 && (
          <Box
            borderRadius="md"
            bg={placeholderBg}
            borderWidth="2px"
            borderColor={borderColor}
            borderStyle="dashed"
            p={8}
            display="flex"
            alignItems="center"
            justifyContent="center"
            minHeight="300px"
            opacity="0.8"
          >
            <VStack spacing={3}>
              <Text fontSize="2xl" opacity="0.6">+</Text>
              <Text fontSize="sm" color={textColor} textAlign="center">
                More projects<br />coming soon
              </Text>
            </VStack>
          </Box>
        )}
      </SimpleGrid>
      
      {/* Innovative Call to Action */}
      <Box
        mt={16}
        position="relative"
        overflow="hidden"
        borderRadius="lg"
        boxShadow="xl"
      >
        {/* Background with gradient and pattern */}
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          bg="blue.600"
          backgroundImage="linear-gradient(135deg, #3182CE 0%, #2C5282 100%)"
          zIndex="0"
        />
        
        {/* Decorative elements */}
        <Box
          position="absolute"
          top="-20px"
          right="-20px"
          width="150px"
          height="150px"
          borderRadius="full"
          bg="whiteAlpha.200"
          zIndex="0"
        />
        <Box
          position="absolute"
          bottom="-30px"
          left="-30px"
          width="120px"
          height="120px"
          borderRadius="full"
          bg="whiteAlpha.100"
          zIndex="0"
        />
        
        {/* Content */}
        <Flex
          direction={{ base: "column", md: "row" }}
          align="center"
          justify="space-between"
          p={{ base: 8, md: 10 }}
          position="relative"
          zIndex="1"
        >
          <Box maxW={{ base: "100%", md: "60%" }} mb={{ base: 6, md: 0 }}>
            <Heading 
              as="h2" 
              size="lg" 
              color="white" 
              mb={4}
              fontWeight="bold"
            >
              Ready to bring your ideas to life?
            </Heading>
            <Text color="whiteAlpha.900" fontSize="md" maxW="500px">
              Whether you need a full-stack application, industrial automation solution, or hardware integration,
              I'm ready to collaborate and help turn your vision into reality.
            </Text>
          </Box>
          
          <Box>
            <Button
              as={RouterLink}
              to="/contact"
              size="lg"
              bg="white"
              color="blue.600"
              _hover={{ bg: "gray.100" }}
              rightIcon={<FaArrowRight />}
              boxShadow="md"
              fontWeight="medium"
              px={8}
            >
              Let's Connect
            </Button>
          </Box>
        </Flex>
      </Box>

    </Container>
    </>
  );
};

export default Projects;








